// Category page targeting the everyday carry community: "edc wallet",
// "best edc wallet", "edc wallet with key holder". The wedge is
// consolidation: wallet + keys + tracker as one pocket item instead of three.
import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import {
  makeBreadcrumbJsonLd,
  makeFaqJsonLd,
} from "../seo/structuredData";
import heroPhoto from "../assets/newest-version/Full - nothing extended just basic.jpg";
import fullPhoto from "../assets/newest-version/Full Front - Everything extended.jpg";

const futura = "'Futura', sans-serif";

const EDC_FAQ = [
  {
    q: "What is an EDC wallet?",
    a: "An EDC (everyday carry) wallet is a compact, durable wallet designed for intentional daily carry: only what you need, in the smallest reliable package. The Brik is an EDC wallet machined from anodized aluminum that consolidates cards, ID, cash, keys, and a tracker into one pocket item.",
  },
  {
    q: "What makes The Brik different from other EDC wallets?",
    a: "Consolidation. Most EDC setups still mean three pocket items: a card wallet, a key carrier, and a tracker. The Brik integrates all three: 7-8 cards plus ID, an included removable keyring, and a rechargeable card-shaped tracker inside the body.",
  },
  {
    q: "How many cards and keys does it hold?",
    a: "1 ID in the quick-access front slot, 7-8 cards in the RFID-protected main compartment, folded cash under the back elastic band, and everyday keys on the integrated keyring, which swaps for any ring you prefer.",
  },
  {
    q: "Does the tracker add bulk?",
    a: "No. The tracker is shaped like a card and slides into its own slot in the body, so the wallet stays flat. It's removable, lasts up to 6 months per charge, and recharges on any wireless charger.",
  },
  {
    q: "What is it made of?",
    a: "Solid black anodized aluminum, machined, with an elastic retention system and a water-resistant body. If the body structurally fails during normal use, we replace it under warranty.",
  },
  {
    q: "Is it front-pocket friendly?",
    a: "Yes. The Brik is designed for front-pocket carry: flat profile, no fold, and the keys ride flush against the body instead of jangling loose.",
  },
];

const EDC_SPECS = [
  ["Body", "Machined anodized aluminum, black"],
  ["Weight & size", "1.2 oz (34 g), 8.5 x 5.5 x 1.5 cm"],
  ["Capacity", "1 ID + 7-8 cards + folded cash"],
  ["Keys", "Integrated removable keyring, included"],
  ["Tracker", "Rechargeable tracking card, included, removable"],
  ["Battery", "Up to 6 months per charge, wireless charging"],
  ["RFID", "Main compartment protected; front ID slot scannable"],
  ["Carry", "Front-pocket, no fold, water-resistant body"],
  ["Price", "$69.99, everything included"],
];

const sectionHeading = {
  fontFamily: futura,
  fontSize: "clamp(26px, 3vw, 36px)",
  margin: "0 0 12px",
  letterSpacing: "-0.5px",
  color: "#111",
};

const bodyText = {
  fontFamily: futura,
  fontSize: "16px",
  lineHeight: 1.65,
  color: "#444",
  margin: "0 0 16px",
};

const ctaButton = {
  display: "inline-block",
  padding: "16px 32px",
  background: "#ff7d1a",
  color: "white",
  borderRadius: "10px",
  fontWeight: 700,
  fontSize: "15px",
  letterSpacing: "1px",
  fontFamily: futura,
  textTransform: "uppercase",
  textDecoration: "none",
  boxShadow: "0 6px 20px rgba(255,125,26,0.35)",
};

const EdcWallet = () => {
  const faqJsonLd = makeFaqJsonLd(EDC_FAQ);
  const breadcrumbJsonLd = makeBreadcrumbJsonLd([
    { name: "Home", path: "/" },
    { name: "EDC Wallet", path: "/edc-wallet" },
  ]);

  return (
    <div style={{ background: "white", minHeight: "100vh" }}>
      <SEO
        title="EDC Wallet with Key Holder & Built-In Tracker"
        description="The Brik is an EDC wallet that consolidates your whole carry: 7-8 cards, ID, cash, keys on an integrated ring, and a rechargeable tracker, in one aluminum body. $69.99."
        path="/edc-wallet"
        keywords="edc wallet, everyday carry wallet, best edc wallet, edc wallet with key holder, minimalist edc, aluminum edc wallet, edc consolidation, one piece edc"
        jsonLd={[faqJsonLd, breadcrumbJsonLd]}
      />
      <Header />

      {/* Hero */}
      <section
        style={{
          background: "radial-gradient(ellipse at top, #2a2a2a 0%, #0c0c0c 70%)",
          color: "white",
          padding: "70px 24px 80px",
        }}
      >
        <div style={{ maxWidth: "840px", margin: "0 auto", textAlign: "center" }}>
          <p
            style={{
              fontFamily: futura,
              color: "#ff7d1a",
              margin: "0 0 10px",
              fontSize: "13px",
              fontWeight: 700,
              letterSpacing: "2px",
            }}
          >
            EVERYDAY CARRY
          </p>
          <h1
            style={{
              fontFamily: futura,
              fontSize: "clamp(30px, 4.2vw, 52px)",
              lineHeight: 1.1,
              letterSpacing: "-1px",
              margin: "0 0 18px",
            }}
          >
            The EDC Wallet That Consolidates Your Whole Carry
          </h1>
          <p
            style={{
              fontFamily: futura,
              fontSize: "17px",
              lineHeight: 1.6,
              color: "#cfcfcf",
              maxWidth: "660px",
              margin: "0 auto 26px",
            }}
          >
            Most EDC setups are still three pocket items: a card wallet, a key
            carrier, and a tracker. The Brik is one: 7-8 cards plus ID behind
            RFID, folded cash, keys on an integrated ring, and a rechargeable
            card-shaped tracker inside a machined aluminum body. $69.99,
            everything included.
          </p>
          <a href="/metal-wallet" style={ctaButton}>
            Get the Metal Brik · $69.99
          </a>
          <p
            style={{
              fontFamily: futura,
              fontSize: "13px",
              color: "#9be8b4",
              margin: "18px 0 0",
              fontWeight: 600,
            }}
          >
            In stock · arrives in 5-7 days · 30-day exchange guarantee
          </p>
          <div style={{ maxWidth: "560px", margin: "36px auto 0" }}>
            <img
              src={heroPhoto}
              alt="Aluminum EDC wallet: The Brik closed with keys attached, front-pocket profile"
              width={1024}
              height={1024}
              fetchPriority="high"
              style={{ width: "100%", height: "auto", borderRadius: "16px", display: "block" }}
            />
          </div>
        </div>
      </section>

      {/* Consolidation argument */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div
          style={{
            maxWidth: "900px",
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "36px",
            alignItems: "center",
          }}
        >
          <div>
            <h2 style={sectionHeading}>Three slots. One item.</h2>
            <p style={bodyText}>
              EDC is about intention: carry exactly what you need and know
              where it is. The usual answer still spreads across pockets, a
              card holder here, a key organizer there, a tracker dropped
              somewhere in between, each bought separately.
            </p>
            <p style={bodyText}>
              The Brik collapses the stack. Cards and ID live behind RFID,
              cash rides the back band, keys mount flush on the integrated
              ring, and the tracker is a card in its own slot rather than a
              coin in a pouch. One item to pocket, one item to check, one item
              your phone can find.
            </p>
            <p style={{ ...bodyText, fontSize: "14px", color: "#666", margin: 0 }}>
              Deciding between plates and frames? Read{" "}
              <a href="/brik-vs-ridge" style={{ color: "#ff7d1a", fontWeight: 600 }}>
                Brik vs Ridge
              </a>
              , or dig into{" "}
              <a
                href="/wallet-with-built-in-tracker"
                style={{ color: "#ff7d1a", fontWeight: 600 }}
              >
                how the built-in tracker works
              </a>
              .
            </p>
          </div>
          <div>
            <img
              src={fullPhoto}
              alt="The Brik EDC wallet with ID, cards, cash, and keys all deployed"
              loading="lazy"
              width={1024}
              height={1024}
              style={{ width: "100%", height: "auto", borderRadius: "16px", display: "block" }}
            />
          </div>
        </div>
      </section>

      {/* Spec table */}
      <section style={{ background: "#f7f7f7", padding: "70px 24px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "28px" }}>
            The spec sheet
          </h2>
          <div
            style={{
              background: "white",
              borderRadius: "14px",
              border: "1px solid #e6e6e6",
              overflow: "hidden",
            }}
          >
            {EDC_SPECS.map(([label, value], i) => (
              <div
                key={label}
                style={{
                  display: "flex",
                  gap: "16px",
                  padding: "14px 20px",
                  borderTop: i === 0 ? "none" : "1px solid #eee",
                  fontFamily: futura,
                  fontSize: "14.5px",
                }}
              >
                <span
                  style={{
                    flex: "0 0 110px",
                    fontWeight: 700,
                    color: "#ff7d1a",
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                    fontSize: "12.5px",
                    paddingTop: "1px",
                  }}
                >
                  {label}
                </span>
                <span style={{ color: "#333", lineHeight: 1.5 }}>{value}</span>
              </div>
            ))}
          </div>
          <p
            style={{
              fontFamily: futura,
              fontSize: "14px",
              color: "#666",
              textAlign: "center",
              margin: "24px 0 0",
            }}
          >
            Full details, photos, and the 3D model live on the{" "}
            <a href="/metal-wallet" style={{ color: "#ff7d1a", fontWeight: 600 }}>
              Metal Brik product page
            </a>
            .
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "30px" }}>
            EDC questions, answered
          </h2>
          {EDC_FAQ.map((item) => (
            <details
              key={item.q}
              style={{
                background: "#f7f7f7",
                borderRadius: "12px",
                border: "1px solid #e6e6e6",
                padding: "18px 22px",
                marginBottom: "12px",
                fontFamily: futura,
              }}
            >
              <summary
                style={{
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: "15px",
                  color: "#111",
                }}
              >
                {item.q}
              </summary>
              <p
                style={{
                  margin: "10px 0 0",
                  fontSize: "15px",
                  lineHeight: 1.55,
                  color: "#555",
                }}
              >
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section
        style={{
          background: "#0c0c0c",
          color: "white",
          padding: "70px 24px",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: "640px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, color: "white" }}>
            Carry less. Lose nothing.
          </h2>
          <p
            style={{
              fontFamily: futura,
              fontSize: "16px",
              lineHeight: 1.6,
              color: "#cfcfcf",
              margin: "0 0 26px",
            }}
          >
            One aluminum body for cards, ID, cash, keys, and tracking. $69.99,
            in stock, arrives in 5-7 days.
          </p>
          <a href="/metal-wallet" style={ctaButton}>
            Buy the Metal Brik
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EdcWallet;
